import { z } from 'zod';
import { tryCatch } from '@shared/helpers/utils.helpers';
import { AI_MODEL_USE_CASES } from './ai-model-use-cases.model';
import {
  OPEN_AI_ALLOWED_MODELS,
  OPEN_AI_FILE_DETAILS,
  isAllowedOpenAiModel,
  type OpenAiAllowedModels,
  type OpenAiFileDetail,
} from './ai-models.model';
import {
  OPEN_AI_REASONING_EFFORTS,
  getOpenAiReasoningSupport,
  type OpenAiReasoningEffort,
} from './open-ai-reasoning.model';

/**
 * Project-file pipeline steps that call a model. Segment extraction is split
 * so drawings and text documents can get their own model, effort and detail.
 */
export const PROJECT_FILE_PIPELINE_AI_STEPS = [
  'classify',
  'drawingDetection',
  'drawingFootprint',
  'drawingScale',
  'siteModel',
  'drawingSegmentExtraction',
  'textSegmentExtraction',
  'joinedBlueprintExtraction',
  'consolidation',
  /** Removed; kept so batches that ran it still read their AI calls. */
  'ffuCorrection',
  'ffuProjectLocation',
  'ffuChatMessage',
] as const;
export const projectFilePipelineAiStepSchema = z.enum(
  PROJECT_FILE_PIPELINE_AI_STEPS,
);
export type ProjectFilePipelineAiStep = z.infer<
  typeof projectFilePipelineAiStepSchema
>;

/** Step names stored or configured before a rename, by their current name. */
const RENAMED_STEPS: Readonly<Record<string, ProjectFilePipelineAiStep>> = {
  ffuChoicesSummary: 'ffuChatMessage',
};

const currentStepName = (value: unknown): unknown =>
  typeof value === 'string' ? (RENAMED_STEPS[value] ?? value) : value;

/** A step as stored in `ai_calls`, where a renamed step keeps its old name. */
export const storedProjectFilePipelineAiStepSchema = z.preprocess(
  currentStepName,
  projectFilePipelineAiStepSchema,
);

export const openAiFileDetailSchema = z.enum(OPEN_AI_FILE_DETAILS);

/** Model settings one pipeline step runs with. */
export type ProjectFilePipelineStepAi = {
  model: OpenAiAllowedModels;
  /** `null` omits `reasoning.effort`, so the model default applies. */
  reasoningEffort: OpenAiReasoningEffort | null;
  /** Ignored by steps that attach no file. */
  fileDetail: OpenAiFileDetail;
};

/** Registry use case that owns each step's default model. */
const STEP_USE_CASE = {
  classify: 'projectFileClassifier',
  drawingDetection: 'projectFileDrawingDetection',
  drawingFootprint: 'projectFileDrawingFootprint',
  drawingScale: 'projectFileDrawingScale',
  siteModel: 'projectFileSiteModel',
  drawingSegmentExtraction: 'projectFileSegmentExtraction',
  textSegmentExtraction: 'projectFileSegmentExtraction',
  joinedBlueprintExtraction: 'projectFileBlueprintExtraction',
  consolidation: 'projectFileLabelConsolidation',
  ffuCorrection: 'projectFileSiteModel',
  ffuProjectLocation: 'ffuProjectLocation',
  ffuChatMessage: 'projectFileLabelConsolidation',
} as const satisfies Record<
  ProjectFilePipelineAiStep,
  keyof typeof AI_MODEL_USE_CASES
>;

const DEFAULT_FILE_DETAIL: Record<ProjectFilePipelineAiStep, OpenAiFileDetail> =
  {
    classify: 'auto',
    drawingDetection: 'high',
    drawingFootprint: 'high',
    drawingScale: 'high',
    siteModel: 'high',
    drawingSegmentExtraction: 'high',
    textSegmentExtraction: 'low',
    joinedBlueprintExtraction: 'high',
    consolidation: 'auto',
    ffuCorrection: 'high',
    ffuProjectLocation: 'auto',
    ffuChatMessage: 'auto',
  };

const stepOverrideSchema = z
  .object({
    model: z.enum(OPEN_AI_ALLOWED_MODELS).optional(),
    reasoningEffort: z.enum(OPEN_AI_REASONING_EFFORTS).optional(),
    fileDetail: openAiFileDetailSchema.optional(),
  })
  .strict();

/** Per-step overrides, e.g. `{ "classify": { "model": "gpt-5.6-luna", "reasoningEffort": "low" } }`. */
export const projectFilePipelineAiOverridesSchema = z.preprocess(
  (value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(
          Object.entries(value).map(([step, override]) => [
            currentStepName(step),
            override,
          ]),
        )
      : value,
  z.partialRecord(projectFilePipelineAiStepSchema, stepOverrideSchema),
);
export type ProjectFilePipelineAiOverrides = z.infer<
  typeof projectFilePipelineAiOverridesSchema
>;

type ResolveOptions = {
  overrides: ProjectFilePipelineAiOverrides;
  /** Global `OPEN_AI_MODEL`. Applies to every step without its own model override. */
  globalModelOverride: string | undefined;
};

const resolveStepModel = (
  step: ProjectFilePipelineAiStep,
  options: ResolveOptions,
): OpenAiAllowedModels => {
  const stepModel = options.overrides[step]?.model;
  if (stepModel) return stepModel;
  if (isAllowedOpenAiModel(options.globalModelOverride)) {
    return options.globalModelOverride;
  }
  return AI_MODEL_USE_CASES[STEP_USE_CASE[step]].defaultModel;
};

const isEffortSupported = (
  model: OpenAiAllowedModels,
  effort: OpenAiReasoningEffort,
): boolean =>
  getOpenAiReasoningSupport(model)?.efforts.some((e) => e === effort) ?? false;

/**
 * Parses the `PROJECT_FILE_PIPELINE_AI` env JSON. Unset or blank means no
 * overrides. Rejects a reasoning effort the step's resolved model does not
 * accept, so {@link resolveProjectFilePipelineStepAi} can trust the value.
 */
export const parseProjectFilePipelineAiOverrides = (
  raw: string | undefined,
  options: Pick<ResolveOptions, 'globalModelOverride'>,
): ProjectFilePipelineAiOverrides | Error => {
  if (raw === undefined || raw.trim() === '') return {};
  const json = tryCatch(() => JSON.parse(raw) as unknown);
  if (!json.ok) return new Error('PROJECT_FILE_PIPELINE_AI is not valid JSON');
  const parsed = projectFilePipelineAiOverridesSchema.safeParse(json.value);
  if (!parsed.success) {
    return new Error(
      `PROJECT_FILE_PIPELINE_AI is invalid: ${parsed.error.message}`,
    );
  }
  const unsupported = PROJECT_FILE_PIPELINE_AI_STEPS.flatMap((step) => {
    const effort = parsed.data[step]?.reasoningEffort;
    if (!effort) return [];
    const model = resolveStepModel(step, {
      overrides: parsed.data,
      globalModelOverride: options.globalModelOverride,
    });
    return isEffortSupported(model, effort)
      ? []
      : [`${step}: ${model} does not accept reasoning effort "${effort}"`];
  });
  if (unsupported.length > 0) {
    return new Error(
      `PROJECT_FILE_PIPELINE_AI is invalid: ${unsupported.join('; ')}`,
    );
  }
  return parsed.data;
};

/**
 * Settings for one step. Model precedence: step override, then the global
 * `OPEN_AI_MODEL`, then the step's registry default. Overrides must come from
 * {@link parseProjectFilePipelineAiOverrides}.
 */
export const resolveProjectFilePipelineStepAi = (
  step: ProjectFilePipelineAiStep,
  options: ResolveOptions,
): ProjectFilePipelineStepAi => {
  const override = options.overrides[step];
  return {
    model: resolveStepModel(step, options),
    reasoningEffort: override?.reasoningEffort ?? null,
    fileDetail: override?.fileDetail ?? DEFAULT_FILE_DETAIL[step],
  };
};
